import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiLayout, FiCode, FiRocket, FiMail } = FiIcons;

const Process = () => {
  const steps = [
    {
      icon: FiSearch,
      title: "Discovery & Analysis",
      description: "We analyze your current infrastructure, identify pain points, and map out transformation opportunities.",
      color: "from-blue-500 to-cyan-500",
      image: "https://bmis-local.s3.ap-southeast-1.amazonaws.com/public/bmis-analysis.jpg"
    },
    {
      icon: FiLayout,
      title: "Strategy & Design",
      description: "Create a comprehensive roadmap with cloud-native architecture and open-source technology selection.",
      color: "from-indigo-500 to-purple-500",
      image: "https://bmis-local.s3.ap-southeast-1.amazonaws.com/public/bmis-strategy.jpg"
    },
    {
      icon: FiCode,
      title: "Engineer & Integrations",
      description: "Build and integrate solutions using agile methodologies and continuous deployment practices.",
      color: "from-purple-500 to-pink-500",
      image: "https://bmis-local.s3.ap-southeast-1.amazonaws.com/public/bmis-dev-integrations.jpg"
    },
    {
      icon: FiRocket,
      title: "Launch & Optimization",
      description: "Deploy your transformed digital ecosystem with ongoing monitoring and performance optimization.",
      color: "from-green-500 to-teal-500",
      image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&h=300&fit=crop"
    }
  ];

  return (
    <section id="process" className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Our Proven <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">Transformation Process</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A systematic approach to digital transformation that ensures successful outcomes and measurable business impact at every stage.
          </p>
        </motion.div>

        <div className="relative mb-20">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transform -translate-y-1/2"></div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="relative"
              >
                {/* Step Number */}
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm z-10">
                  {index + 1}
                </div>

                <motion.div
                  whileHover={{ y: -10 }}
                  className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300 pt-12 group"
                >
                  {/* Image Container */}
                  <div className="relative mb-6 overflow-hidden rounded-xl">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className={`w-10 h-10 bg-gradient-to-r ${step.color} rounded-lg flex items-center justify-center`}>
                        <SafeIcon icon={step.icon} className="text-white text-lg" />
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-4 text-center">{step.title}</h3>
                  <p className="text-gray-300 text-center leading-relaxed text-sm">{step.description}</p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative bg-gradient-to-r from-indigo-500/10 to-purple-500/10 backdrop-blur-lg rounded-3xl p-12 lg:p-16 border border-white/10 text-center">
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto"
          >
         Fast-forward your vision into a successful tech startup with our Phoinix Package using the most breakthrough ecosystem and accelerate your business growth.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="w-full max-w-7xl mx-auto mb-8"
          >
            <img 
              src="https://bmis-local.s3.ap-southeast-1.amazonaws.com/public/bmis-phoinix-programm.png" 
              alt="Phoinix Program" 
              className="w-full h-auto rounded-2xl shadow-2xl border border-white/10"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
            className="flex items-center justify-center space-x-2 text-gray-400"
          >
            <SafeIcon icon={FiMail} className="text-lg" />
            <span>contact@bmi-software.gr</span>
          </motion.div>
        </div>
          
      </div>
    </section>
  );
};

export default Process;